import React, { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { Card } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from '../utils/toast';
import { Search, RefreshCw, MessageSquare } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactSubmissionsPage = () => {
  const [submissions, setSubmissions] = useState([]);
  const [filteredSubmissions, setFilteredSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchSubmissions();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [searchQuery, submissions]);

  const fetchSubmissions = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/contact-submissions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubmissions(response.data);
      setFilteredSubmissions(response.data);
    } catch (error) {
      toast.error('Failed to fetch contact submissions');
      console.error('Error fetching contact submissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...submissions];
    
    if (searchQuery) {
      filtered = filtered.filter(sub =>
        sub.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        sub.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        sub.interested_for?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        sub.phone_number?.includes(searchQuery) ||
        sub.message?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    setFilteredSubmissions(filtered);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getInterestBadgeColor = (interest) => {
    const colors = {
      'Product Demo': 'bg-blue-100 text-blue-800',
      'Enterprise Plan': 'bg-purple-100 text-purple-800',
      'Pricing Information': 'bg-green-100 text-green-800',
      'Technical Support': 'bg-orange-100 text-orange-800',
      'Partnership': 'bg-pink-100 text-pink-800',
      'General Inquiry': 'bg-gray-100 text-gray-800'
    };
    return colors[interest] || 'bg-gray-100 text-gray-800';
  };

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Contact Submissions</h1>
          <p className="text-gray-600">View and manage contact form submissions from the website</p>
        </div>

      {/* Filters */}
      <Card className="p-6 mb-6">
        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              placeholder="Search by name, email, phone, interest, or message..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Button onClick={fetchSubmissions} variant="outline" disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </Card>

      {/* Submissions Table */}
      <Card>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Full Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Interested For</TableHead>
                <TableHead>Message</TableHead>
                <TableHead>Submitted On</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                    Loading contact submissions...
                  </TableCell>
                </TableRow>
              ) : filteredSubmissions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                    No contact submissions found
                  </TableCell>
                </TableRow>
              ) : (
                filteredSubmissions.map((submission, index) => (
                  <TableRow key={submission.id || index}>
                    <TableCell className="font-medium">{submission.full_name}</TableCell>
                    <TableCell>
                      <a href={`mailto:${submission.email}`} className="text-blue-600 hover:underline">
                        {submission.email}
                      </a>
                    </TableCell>
                    <TableCell>
                      <a href={`tel:${submission.phone_number}`} className="text-blue-600 hover:underline">
                        {submission.phone_number}
                      </a>
                    </TableCell>
                    <TableCell>
                      <Badge className={getInterestBadgeColor(submission.interested_for)}>
                        {submission.interested_for || 'N/A'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="max-w-xs">
                        <p className="text-sm text-gray-600 truncate" data-tooltip={submission.message}>
                          {submission.message || '-'}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>{formatDate(submission.created_at)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Summary */}
        <div className="border-t border-gray-200 px-6 py-4">
          <p className="text-sm text-gray-600">
            Showing {filteredSubmissions.length} of {submissions.length} contact submissions
          </p>
        </div>
      </Card>
      </div>
    </AdminLayout>
  );
};

export default ContactSubmissionsPage;
